export type SkillResourceType = 'reference' | 'script';

export interface SkillResource {
  type: SkillResourceType;
  path: string;
  fileId: string;
  fileName: string;
  size: number;
  contentType: string;
  content: string;
  language: string | null;
}

export interface SkillListItem {
  id: string;
  name: string;
  displayName?: string | null;
  description: string | null;
  instructions: string | null;
  version: string | null;
  iconUrl: string | null;
  tags: string[];
  requiredToolIds: string[];
  requiredIntegrationTypes: string[];
  resources: SkillResource[];
  scripts: unknown[];
  scope: string | null;
  visibility: string | null;
  forceEnabled: boolean;
  isSystem?: boolean;
  installCount: number;
  tenantId: string;
  createdBy: string;
  /** Server-computed write access (owner or org admin). */
  canEdit?: boolean | null;
  createdDate: string;
  lastUpdatedDate: string;
  installed: boolean;
}

export type SkillDetails = SkillListItem;

export interface GetSkillsResponse {
  totalCount: number;
  page: number;
  pageSize: number;
  data: SkillListItem[];
}

export interface GetSkillsPayload {
  page: number;
  pageSize: number;
  search_query?: string;
  surface?: string;
  installed?: boolean;
  visibility?: SkillVisibility;
  order?: 'asc' | 'desc';
  order_by?: 'last_updated' | 'created' | 'name' | 'install_count';
  pack_id?: string;
}

export const getSkillDisplayName = (skill: Pick<SkillListItem, 'displayName' | 'name'>) =>
  skill.displayName?.trim() || skill.name;

export type SkillVisibility = 'private' | 'shared' | 'public';

export interface CreateSkillPayload {
  name: string;
  displayName?: string;
  description: string;
  instructions: string;
  tags: string[];
  requiredToolIds: string[];
  requiredIntegrationTypes: string[];
  scope: string;
  visibility: SkillVisibility;
}

/** Partial PUT body — send only fields that changed. Mirrors backend
 * `UpdateSkillRequest`, which is fully optional. */
export interface UpdateSkillPayload {
  name?: string;
  displayName?: string;
  description?: string;
  instructions?: string;
  version?: string;
  iconUrl?: string | null;
  tags?: string[];
  requiredToolIds: string[];
  requiredIntegrationTypes: string[];
  scope: string;
  visibility: string;
  resources?: SkillResource[];
}

export interface ImportSkillPayload {
  file: File;
  visibility?: string;
}

export interface ImportSkillResponse {
  detail?: string;
  message?: string;
  data?: SkillDetails | SkillDetails[] | null;
}

export interface DraftSkillResponse {
  name?: string | null;
  description?: string | null;
  instructions?: string | null;
  tags: string[];
  requiredIntegrationTypes: string[];
}

export type SkillOptimizableField = 'description' | 'instructions';

export interface OptimizeSkillFieldPayload {
  field: SkillOptimizableField;
  value: string;
  context?: Record<string, unknown>;
}

export interface OptimizeSkillFieldResponse {
  value: string;
}

export interface UpdateSkillResourcePayload {
  skillId: string;
  fileId: string;
  content: string;
}
